import "./Checkbox.css";
import checkBoxIcon from "@ms/check_box.svg?raw";
import checkBoxOutlineBlankIcon from "@ms/check_box_outline_blank.svg?raw";
import indeterminateCheckBoxIcon from "@ms/indeterminate_check_box.svg?raw";
import {
	type Component,
	createEffect,
	type JSX,
	Match,
	Switch,
} from "solid-js";
import { Icon } from "./Icon";

type Props = {
	id: string;
	checked: boolean | null;
	label?: string;
	/** Accessible name when there is no visible {@link label} (e.g. a table cell). */
	ariaLabel?: string;
	disabled?: boolean;
	/** @default "medium" */
	size?: "medium" | "small";
	/** @default "primary" */
	theme?: "primary" | "ink";
	onChange?: JSX.EventHandlerUnion<HTMLInputElement, Event>;
	onClick?: JSX.EventHandlerUnion<HTMLElement, MouseEvent>;
};

/**
 * Checkbox input with optional indeterminate state and label.
 *
 * [Design System →](http://localhost:5174/component/Form%20Controls)
 */
export const Checkbox: Component<Props> = (props) => {
	let inputEl: HTMLInputElement | undefined;

	createEffect(() => {
		if (!inputEl) return;
		// `indeterminate` is a DOM property (not an HTML attribute).
		inputEl.indeterminate = props.checked === null;
	});

	return (
		<span
			class="gvtui-checkbox-container"
			classList={{
				"gvtui-checkbox--small": (props.size ?? "medium") === "small",
				"gvtui-checkbox--theme-ink": (props.theme ?? "primary") === "ink",
			}}
		>
			<input
				type="checkbox"
				id={props.id}
				aria-label={props.label ? undefined : props.ariaLabel}
				aria-describedby={props.label ? props.id + "-label" : undefined}
				ref={(el) => {
					inputEl = el;
					el.indeterminate = props.checked === null;
				}}
				checked={props.checked === true}
				disabled={props.disabled}
				onChange={props.onChange}
				onClick={props.onClick}
			/>
			<label
				for={props.id}
				class="gvtui-checkbox-image"
				id={props.id + "-image"}
			>
				<Icon icon={checkBoxIcon} class="gvtui-checkbox-icon-checked" />
				<Icon icon={checkBoxOutlineBlankIcon} class="gvtui-checkbox-icon-unchecked" />
				<Icon
					icon={indeterminateCheckBoxIcon}
					class="gvtui-checkbox-icon-indeterminate"
				/>
			</label>
			<Switch>
				<Match when={props.label}>
					<label
						for={props.id}
						class="gvtui-checkbox-label"
						id={props.id + "-label"}
					>
						{props.label}
					</label>
				</Match>
			</Switch>
		</span>
	);
};
